import { useState, useEffect } from "react";
import { Mail, Share2, Download, CheckCircle2 } from "lucide-react";
import { canShareFiles, sharePdf, downloadFile, bytesToBase64, brandLine } from "./deliver.js";
import { mailtoLink } from "./biz.js";

// "Email this PDF" — picks the best way available on this device and says plainly what it will do.
//   server   : paid account → our server emails it with the PDF attached (fully automatic)
//   share    : phone / recent browsers → share sheet with the PDF file already attached
//   fallback : browser can't attach files → download the PDF and open a drafted email
export function DeliverBox({ makeFile, fileKey, subject, body, defaultTo = "", businessName, replyTo, ctx, kind = "invoice", invoiceId, onSent, onRecipient, label = "Email to", branding = true }) {
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");
  const [to, setTo] = useState(defaultTo);
  const [state, setState] = useState("idle"); // idle | sending | sent | opened
  const [msg, setMsg] = useState("");
  const [copyMe, setCopyMe] = useState(true);

  useEffect(() => {
    let alive = true;
    setFile(null); setErr("");
    makeFile().then((f) => { if (alive) setFile(f); }).catch((e) => { if (alive) setErr(`Couldn't build the PDF: ${e.message}`); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  const valid = /^\S+@\S+\.\S+$/.test(to.trim());
  const server = !!(ctx?.isPaid && ctx?.config?.emailEnabled);
  const share = !server && canShareFiles(file);
  const mode = server ? "server" : share ? "share" : "fallback";
  const busy = state === "sending";
  const ready = !!file && !err;

  async function go() {
    setMsg("");
    try {
      if (mode === "server") {
        setState("sending");
        const bytes = new Uint8Array(await file.arrayBuffer());
        await ctx.send({ to: to.trim(), subject, text: body, pdfBase64: bytesToBase64(bytes), filename: file.name, businessName, replyTo, kind, invoiceId, branding, copyMe: copyMe && !!replyTo });
        onRecipient?.(to.trim());
        setState("sent"); onSent?.("server");
        return;
      }
      if (mode === "share") {
        await sharePdf(file, { title: subject, text: branding ? `${body}\n\n${brandLine()}` : body });   // straight from the click: iOS requires it
        if (valid) onRecipient?.(to.trim());
        setState("opened");
        return;
      }
      downloadFile(file);
      const url = mailtoLink(to.trim(), subject, `${body}${branding ? `\n\n${brandLine()}` : ""}\n\n[Attach the PDF that just downloaded: ${file.name}]`);
      if (valid) onRecipient?.(to.trim());
      window.location.href = url;
      setState("opened");
    } catch (e) {
      setState("idle");
      if (e?.name !== "AbortError") setMsg(e.message || "That didn't work.");
    }
  }

  const btn = { server: "Send email with PDF attached", share: "Email with PDF attached", fallback: "Download PDF and open email" }[mode];
  const hint = {
    server: "Sent from our server with the PDF attached. It shows your business name as the sender, and replies go to your own email address.",
    share: "Opens your share sheet. Choose Mail or Gmail and the PDF is already attached.",
    fallback: "This browser can't attach files to an email by itself. We'll download the PDF and open a drafted email; attach the file that just downloaded.",
  }[mode];

  return (
    <div className="bg-card rounded-2xl p-3.5">
      <label className="text-xs text-ivory-dim" htmlFor={`to-${kind}`}>{label}{mode === "server" ? "" : " (optional)"}</label>
      <div className="flex gap-2 mt-1">
        <input id={`to-${kind}`} value={to} onChange={(e) => setTo(e.target.value)} type="email" inputMode="email" placeholder="client@example.com" className="flex-1 min-w-0 rounded-lg px-3 py-2 text-sm" />
      </div>
      <button onClick={go} disabled={!ready || busy || (mode === "server" && !valid)} className="w-full flex items-center justify-center gap-1.5 bg-brass text-ink font-semibold rounded-full py-2.5 text-sm mt-2.5 disabled:opacity-40">
        {mode === "share" ? <Share2 size={14} /> : mode === "server" ? <Mail size={14} /> : <Download size={14} />}
        {busy ? "Sending…" : !ready && !err ? "Preparing PDF…" : btn}
      </button>
      <div className="text-xs text-ivory-dim mt-2 leading-relaxed">{hint}</div>
      {mode === "server" && replyTo && <label className="flex items-center gap-2 text-xs text-ivory-dim mt-2.5 cursor-pointer"><input type="checkbox" checked={copyMe} onChange={(e) => setCopyMe(e.target.checked)} /> Send me a copy at {replyTo}</label>}
      {ctx?.signedIn && !ctx?.isPaid && ctx?.config?.emailEnabled && mode !== "server" && <div className="text-xs text-ivory-dim mt-1">Paid accounts can send straight from the app with no extra steps.</div>}
      {err && <div className="text-xs mt-2" style={{ color: "var(--rust)" }}>{err}</div>}
      {msg && <div className="text-xs mt-2" style={{ color: "var(--rust)" }}>{msg}</div>}
      {state === "sent" && <div className="flex items-center gap-1.5 text-xs mt-2.5" style={{ color: "var(--sage)" }}><CheckCircle2 size={13} /> Sent to {to.trim()} with the PDF attached.</div>}
      {state === "opened" && onSent && (
        <div className="flex items-center justify-between gap-3 bg-card2 rounded-xl px-3 py-2 mt-2.5">
          <span className="text-xs">Did you send it?</span>
          <button onClick={() => { onSent("manual"); setState("idle"); }} className="text-xs font-semibold text-brass">Yes, mark as sent</button>
        </div>
      )}
      {file && <button onClick={() => downloadFile(file)} className="flex items-center gap-1 text-xs text-brass font-medium mt-2.5"><Download size={12} /> Download PDF only</button>}
    </div>
  );
}
